/**
 *  @module Resources/TransactionToken
 */

import { Connector } from "checkout/ts/Connector";
import { TokenError } from "checkout/ts/errors/TokenError";
import { LOCALE_LABELS } from "checkout/ts/locale/labels";
import { isTest } from "checkout/ts/utils/environment";
import { getMessage } from "checkout/ts/utils/intl";
import { POLLING_INTERVAL } from "common/constants";
import { TimeoutError } from "common/errors/TimeoutError";
import { SupportedLocale } from "common/locale/labels";
import {
    AuthParams,
    CvvAuthorizedStatus,
    DefinedRoute,
    HTTPMethod,
    PaymentError,
    PaymentType,
    TransactionTokenCardDataItem,
    TransactionTokenItem,
    TransactionTokens as NodeTransactionToken,
} from "univapay-node";

import { PatchedThreeDsTransactionToken } from "../../utils/token";

type PollOptions = {
    successCondition: (token: TransactionTokenItem) => boolean;
    failureCondition: (token: TransactionTokenItem) => boolean;
    error: string;

    maxRetry?: number;
    interval?: number;
};

export type IssuerToken = {
    issuerToken: string;
    callMethod: "http_post" | "http_get";
    payload: Record<string, string>;
    paymentType: PaymentType;
    contentType: string;
};

export enum ThreeDsStatus {
    SUCCESSFUL = "successful",
    FAILED = "failed",
    ERROR = "error",
    AWAITING = "awaiting",
    PENDING = "pending",
}

export type PatchedTokenCardData = TransactionTokenCardDataItem & {
    threeDs: {
        enabled: boolean;
        status: ThreeDsStatus;
        error?: PaymentError;
        exempted?: boolean;
    };
};

export class TransactionTokens extends NodeTransactionToken {
    private _issuerToken?: DefinedRoute;
    issuerToken(storeId: string, id: string, auth?: AuthParams): Promise<IssuerToken> {
        this._issuerToken =
            this._issuerToken ??
            this.defineRoute(
                HTTPMethod.GET,
                "/stores/:storeId/tokens/:tokenId/three_ds/issuer_token",
                undefined,
                undefined,
                undefined,
                undefined,
                ["payload"]
            );
        return this._issuerToken(null, null, auth, { storeId, tokenId: id });
    }

    private _enableThreeDs?: DefinedRoute;
    enableThreeDs(storeId: string, id: string, auth?: AuthParams): Promise<PatchedThreeDsTransactionToken> {
        this._enableThreeDs =
            this._enableThreeDs ?? this.defineRoute(HTTPMethod.POST, "/stores/:storeId/tokens/:tokenId/three_ds");
        return this._enableThreeDs(null, null, auth, { storeId, tokenId: id });
    }

    async pollCvvAuth(storeId: string, id: string) {
        return this.poll(storeId, id, {
            successCondition: (token) => token?.data?.cvvAuthorize?.status === CvvAuthorizedStatus.CURRENT,
            failureCondition: (token) => token?.data?.cvvAuthorize?.status === CvvAuthorizedStatus.FAILED,
            error: getMessage(LOCALE_LABELS.ERRORS_ALERTS_TOKEN_CVV_AUTHORIZATION_FAILED),
        });
    }

    async pollPendingThreeDsToken(storeId: string, id: string) {
        return this.poll(storeId, id, {
            maxRetry: -1,
            successCondition: (token) =>
                (token.data as PatchedTokenCardData)?.threeDs?.status !== ThreeDsStatus.PENDING,
            failureCondition: (token) =>
                [ThreeDsStatus.FAILED, ThreeDsStatus.ERROR].includes(
                    (token.data as PatchedTokenCardData)?.threeDs?.status
                ),
            error: getMessage(LOCALE_LABELS.ERRORS_ALERTS_THREE_DS_FAILED),
        });
    }

    async pollThreeDs(storeId: string, id: string, locale: SupportedLocale, connector: Connector) {
        connector.emitter.emit("checkout:three-ds-authorization-open-modal", {
            storeId,
            tokenId: id,
            jwt: this.api.jwtRaw,
            endpoint: this.api.endpoint,
            locale,
        });

        const token = await this.poll(storeId, id, {
            maxRetry: -1,
            successCondition: (token) =>
                (token.data as PatchedTokenCardData)?.threeDs?.status === ThreeDsStatus.SUCCESSFUL || isTest(),
            failureCondition: (token) =>
                [ThreeDsStatus.FAILED, ThreeDsStatus.ERROR].includes(
                    (token.data as PatchedTokenCardData)?.threeDs?.status
                ),
            error: getMessage(LOCALE_LABELS.ERRORS_ALERTS_THREE_DS_FAILED),
        });

        return isTest()
            ? {
                  ...token,
                  data: {
                      ...token?.data,
                      threeDs: {
                          ...(token.data as { threeDs: { enabled: boolean; status: ThreeDsStatus } })?.threeDs,
                          status: token.email.includes("+3ds-failure") ? ThreeDsStatus.ERROR : ThreeDsStatus.SUCCESSFUL,
                      },
                  } as PatchedTokenCardData,
              }
            : token;
    }

    async poll(
        storeId: string,
        id: string,
        { maxRetry = 300, interval = POLLING_INTERVAL, successCondition, failureCondition, error }: PollOptions
    ): Promise<TransactionTokenItem> {
        let retryCount = 0;
        let token: TransactionTokenItem;

        do {
            // If it's the first time we poll we skip the delay
            if (retryCount > 0) {
                // Wait for interval. Prevent flooding the API
                await new Promise((resolve) => setTimeout(resolve, interval));
            }

            try {
                token = await this.get(storeId, id);
            } catch (error) {
                console.warn("Could not get transaction token. Waiting for next poll", error);
            }

            retryCount++;
        } while ((maxRetry < 0 || retryCount < maxRetry) && !successCondition(token) && !failureCondition(token));

        if (!successCondition(token) && !failureCondition(token)) {
            throw new TimeoutError(maxRetry * interval);
        }

        if (failureCondition(token)) {
            const tokenThreeDsError = (token.data as PatchedTokenCardData)?.threeDs?.error;
            throw new TokenError(
                token.id,
                "chargeId" in token.data.cvvAuthorize ? token.data.cvvAuthorize.chargeId : null,
                token.storeId,
                tokenThreeDsError ? tokenThreeDsError.message : error,
                tokenThreeDsError ? tokenThreeDsError.code : undefined
            );
        }

        return token;
    }
}
